/**
 * Manual Payments moderation page (admin/directivo).
 * Shows pending alternative-method subscription payments submitted by users.
 * Allows approve/reject with optional notes; opens proof image when uploaded.
 */
import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, XCircle, Receipt, Image as ImageIcon, Clock, Bank, Link as LinkIcon, Hand } from "@phosphor-icons/react";

const STATUS_TABS = [
  { value: "pending", label: "Pendientes", color: "text-yellow-400" },
  { value: "approved", label: "Aprobadas", color: "text-green-400" },
  { value: "rejected", label: "Rechazadas", color: "text-red-400" },
];

const TYPE_ICONS = { bank: Bank, link: LinkIcon, paypal: LinkIcon, manual: Hand };

function fmtCurrency(amount, currency = "MXN") {
  if (amount === 0 || amount == null) return `0 ${currency}`;
  return `$${Number(amount).toLocaleString("es-MX")} ${currency}`;
}

export default function ManualPayments() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [proofOpen, setProofOpen] = useState(null); // request item
  const [proofUrl, setProofUrl] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/manual-payments/?status=${tab}`)
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => toast.error("No se pudo cargar"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const counts = useMemo(() => ({
    pending: items.filter(i => i.status === "pending").length,
  }), [items]);

  const openProof = async (item) => {
    if (!item.proof_path) {
      toast.info("Esta solicitud no incluye comprobante");
      return;
    }
    try {
      const { data } = await api.get(`/manual-payments/${item.id}/proof`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      setProofUrl(url);
      setProofOpen(item);
    } catch {
      toast.error("No se pudo abrir el comprobante");
    }
  };

  const closeProof = () => {
    setProofOpen(null);
    if (proofUrl) URL.revokeObjectURL(proofUrl);
    setProofUrl(null);
  };

  const approve = async (item) => {
    try {
      await api.post(`/manual-payments/${item.id}/approve`, { review_notes: reviewNotes });
      toast.success("Suscripción aprobada");
      setActing(null); setReviewNotes("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aprobar");
    }
  };
  const reject = async (item) => {
    try {
      await api.post(`/manual-payments/${item.id}/reject`, { review_notes: reviewNotes });
      toast.success("Solicitud rechazada");
      setActing(null); setReviewNotes("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al rechazar");
    }
  };

  if (!user || !["admin", "directivo"].includes(user.role)) {
    return <div className="card-tactical p-12 text-center"><p className="text-gray-400">Acceso restringido</p></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="manual-payments-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Suscripciones</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Pagos Manuales</h1>
        <p className="text-sm text-gray-400 mt-1">Aprueba o rechaza los pagos enviados por jugadores y clubes vía métodos alternativos (transferencia, link externo, PayPal o manual).</p>
      </div>

      <div className="inline-flex bg-[#0A0A0A] border border-white/10 p-1" data-testid="status-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={`px-5 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${tab === t.value ? "bg-[#CCFF00] text-black" : `${t.color} hover:text-white`}`}
            data-testid={`tab-${t.value}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="card-tactical p-10 text-center" data-testid="empty-state">
          <Clock size={32} className="text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Sin solicitudes en este estado.</p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="manual-payments-grid">
          {items.map((it) => {
            const Icon = TYPE_ICONS[it.method_type] || Receipt;
            return (
              <div key={it.id} className="card-tactical p-5" data-testid={`mp-row-${it.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={18} weight="duotone" className="text-[#CCFF00]" />
                      <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase">{it.plan_name}</h3>
                      <Badge variant="outline" className="text-[10px] uppercase border-white/10 text-gray-300">{it.method_label}</Badge>
                    </div>
                    <p className="text-xs text-gray-400">{it.user_name} · {it.user_email}</p>
                    <p className="text-xs text-gray-500 mt-1">{new Date(it.created_at).toLocaleString("es-MX")}</p>
                    {it.notes && <p className="text-xs text-gray-300 mt-2 italic">"{it.notes}"</p>}
                    {it.review_notes && (
                      <p className="text-xs text-amber-300 mt-2">Nota de revisión: {it.review_notes}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-['Barlow_Condensed'] text-2xl font-black text-[#CCFF00]">{fmtCurrency(it.amount, it.currency)}</p>
                    <p className="text-[10px] uppercase tracking-widest text-gray-500">{it.months} mes(es)</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {it.proof_path && (
                    <Button size="sm" variant="outline" onClick={() => openProof(it)} className="border-white/10 text-xs uppercase" data-testid={`mp-proof-${it.id}`}>
                      <ImageIcon size={14} className="mr-1" /> Ver comprobante
                    </Button>
                  )}
                  {it.status === "pending" ? (
                    <>
                      <Button size="sm" onClick={() => { setActing({ ...it, action: "approve" }); setReviewNotes(""); }} className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30 text-xs uppercase" data-testid={`mp-approve-${it.id}`}>
                        <CheckCircle size={14} className="mr-1" /> Aprobar
                      </Button>
                      <Button size="sm" onClick={() => { setActing({ ...it, action: "reject" }); setReviewNotes(""); }} className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 text-xs uppercase" data-testid={`mp-reject-${it.id}`}>
                        <XCircle size={14} className="mr-1" /> Rechazar
                      </Button>
                    </>
                  ) : (
                    <Badge variant="outline" className={`uppercase text-[10px] ${it.status === "approved" ? "text-green-400 border-green-500/30" : "text-red-400 border-red-500/30"}`}>
                      {it.status === "approved" ? "Aprobada" : "Rechazada"} · por {it.reviewer_name || "—"}
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Approve/Reject confirm dialog */}
      <Dialog open={!!acting} onOpenChange={(v) => !v && setActing(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-md" data-testid="mp-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">
              {acting?.action === "approve" ? "Aprobar pago" : "Rechazar solicitud"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-300">
            {acting?.action === "approve"
              ? "Esto activará la suscripción del usuario y registrará la transacción como pagada."
              : "El usuario será notificado del rechazo. Puedes dejar el motivo abajo."}
          </p>
          <Textarea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder={acting?.action === "approve" ? "Nota interna (opcional)" : "Motivo del rechazo (opcional)"}
            rows={3}
            className="bg-[#0A0A0A] border-white/20"
            data-testid="mp-review-notes"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setActing(null)} className="border-white/10">Cancelar</Button>
            {acting?.action === "approve" ? (
              <Button onClick={() => approve(acting)} className="bg-green-500 text-black hover:bg-green-400 font-bold uppercase" data-testid="mp-confirm-approve">Aprobar</Button>
            ) : (
              <Button onClick={() => reject(acting)} className="bg-red-500 text-white hover:bg-red-400 font-bold uppercase" data-testid="mp-confirm-reject">Rechazar</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proof viewer dialog */}
      <Dialog open={!!proofOpen} onOpenChange={(v) => !v && closeProof()}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-3xl" data-testid="mp-proof-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">Comprobante</DialogTitle>
          </DialogHeader>
          {proofUrl && (
            proofOpen?.proof_path?.toLowerCase().endsWith(".pdf") ? (
              <iframe src={proofUrl} title="comprobante" className="w-full h-[60vh] bg-white" />
            ) : (
              <img src={proofUrl} alt="comprobante" className="max-h-[70vh] mx-auto" />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
